import {map} from 'lodash'
import axios, {AxiosResponse} from 'axios'
import {
  LedgerData,
  AdvanceItem,
  AgainstAdvance,
  InvoiceItem,
  AgainstInvoice,
  TransactionItem,
  ApiResponse,
  toCamelCase,
  transformKeys,
  TransformedResponse,
  transformResponse,
} from './ViewTransactionsAPI'
import {TRIAL_BALANCE_SAMPLE_RESPONSE} from './SampleResponse'
const API_URL = process.env.REACT_APP_THEME_API_URL
/**
 * Transforms the API response to match the TrialBalanceResp interface
 * with proper groupId and ledgerId values
 */
export function transformTrialBalanceData(apiResponse: any): TrialBalanceResp {
  // console.log(apiResponse)
  // Helper function to transform balance details and add IDs
  const transformBalanceDetails = (
    data: any,
    groupName: string,
    subGroupName: string | null = null
  ): BalanceDetails => {
    // Find group ID if available
    // const groupId =
    //   subGroupName && apiResponse.group_id[groupName]?.[subGroupName]
    //     ? apiResponse.group_id[groupName][subGroupName]
    //     : null
    const groupId = apiResponse.group_id[groupName]?.['None'] || null
    // Transform children if they exist
    // // console.log(data)
    const transformedChildren = data.children?.map((child: any) => {
      if (child.ledger) {
        // console.log(child)
        // This is a ledger entry
        // const ledgerId =
        //   apiResponse.ledger_id[groupName]?.[subGroupName || 'None']?.[child.ledger]?.['None'] ||
        //   null
        const ledgerId =
          apiResponse.ledger_id[groupName]?.[child.subGroup || 'None']?.[child.ledger]?.['None'] ||
          null
        return {
          ledger: child.ledger,
          ledgerId: ledgerId,
          debit: child.debit,
          credit: child.credit,
        }
      } else {
        // This is a subgroup
        const subGroupId = apiResponse.group_id[groupName]?.[child.sub_group] || null

        return {
          subGroup: child.sub_group,
          groupId: subGroupId,
          debit: child.debit,
          credit: child.credit,
          children: child.children?.map((subChild: any) => {
            // apiResponse.ledger_id[groupName]?.[child.sub_group]?.[subChild.parent_ledger]?.[
            //   subChild.sub_ledger
            // ]
            const ledgerId =
              apiResponse.ledger_id[groupName]?.[child.sub_group]?.[subChild.parent_ledger]?.[
                'None'
              ] || null
            let ledgerObj = {
              ledger: subChild.parent_ledger,
              ledgerId: ledgerId,
              debit: subChild.debit,
              credit: subChild.credit,
              children: subChild.children?.map((subLedger: any, index: number, arr: any) => {
                const ledgerId =
                  apiResponse.ledger_id[groupName]?.[child.sub_group]?.[subChild.parent_ledger]?.[
                    subLedger.sub_ledger
                  ] ||
                  apiResponse.ledger_id[groupName]?.[child.sub_group]?.[subChild.parent_ledger]?.[
                    'None'
                  ] ||
                  null
                return {
                  ledgerId,
                  ledger: subLedger.sub_ledger,
                  debit: subLedger.debit,
                  credit: subLedger.credit,
                }
              }),
            }
            if (ledgerObj.children.length === 1 && !ledgerObj.children[0].ledger)
              ledgerObj.children = []
            return ledgerObj
          }),
        }
      }
    })

    // Return transformed balance details
    return {
      group: groupName,
      // subGroup: subGroupName || undefined,
      groupId: groupId || undefined,
      debit: data.debit,
      credit: data.credit,
      children: transformedChildren,
    }
  }

  // Construct the transformed response
  const transformedResponse: TrialBalanceResp = {
    Assets: apiResponse?.Assets && {
      'Non-Current Assets':
        apiResponse?.Assets?.['Non-Current Assets'] &&
        transformBalanceDetails(apiResponse?.Assets?.['Non-Current Assets'], 'Non-Current Assets'),
      'Current Assets':
        apiResponse?.Assets?.['Current Assets'] &&
        transformBalanceDetails(apiResponse?.Assets?.['Current Assets'], 'Current Assets'),
    },
    Liabilities: apiResponse?.Liabilities && {
      'Non-Current Liabilities':
        apiResponse?.Liabilities?.['Non-Current Liabilities'] &&
        transformBalanceDetails(
          apiResponse.Liabilities['Non-Current Liabilities'],
          'Non-Current Liabilities'
        ),

      'Current Liabilities':
        apiResponse?.Liabilities?.['Current Liabilities'] &&
        transformBalanceDetails(
          apiResponse?.Liabilities?.['Current Liabilities'],
          'Current Liabilities'
        ),

      Equity:
        apiResponse?.Liabilities?.['Equity'] &&
        transformBalanceDetails(apiResponse.Liabilities['Equity'], 'Equity'),
    },
    Incomes: apiResponse?.Incomes && {
      // Note: API has "Direct Incomes" but the interface expects "Direct Income"
      'Direct Income':
        apiResponse?.Incomes?.['Direct Incomes'] &&
        transformBalanceDetails(apiResponse.Incomes['Direct Incomes'], 'Direct Incomes'),
      'Indirect Income':
        apiResponse?.Incomes?.['Indirect Incomes'] &&
        transformBalanceDetails(apiResponse.Incomes['Indirect Incomes'], 'Indirect Incomes'),
    },
    Expenses: apiResponse?.Expenses && {
      'Direct Expenses':
        apiResponse?.Expenses?.['Direct Expenses'] &&
        transformBalanceDetails(apiResponse.Expenses['Direct Expenses'], 'Direct Expenses'),
      'Indirect Expenses':
        apiResponse?.Expenses?.['Indirect Expenses'] &&
        transformBalanceDetails(apiResponse.Expenses['Indirect Expenses'], 'Indirect Expenses'),
    },
    differenceAmount: apiResponse.difference_amount,
    totalDebit: apiResponse.total_debit,
    totalCredit: apiResponse.total_credit,
    differencePercentage: apiResponse.difference_percentage,
    group_id: apiResponse.group_id,
    ledger_id: apiResponse.ledger_id,
  }

  return transformedResponse
}
export interface TrialBalanceResp {
  Expenses: {
    'Direct Expenses': BalanceDetails
    'Indirect Expenses': BalanceDetails
  }
  Assets: {
    'Non-Current Assets': BalanceDetails
    'Current Assets': BalanceDetails
  }
  Liabilities: {
    'Non-Current Liabilities': BalanceDetails
    'Current Liabilities': BalanceDetails
    Equity: BalanceDetails
  }
  Incomes: {
    'Direct Income': BalanceDetails
    'Indirect Income': BalanceDetails
  }
  differenceAmount: string
  totalDebit: string
  totalCredit: string
  differencePercentage: number
  group_id: any
  ledger_id: any
}

export interface BalanceDetails {
  group?: string
  subGroup?: string | null
  groupId?: number
  ledger?: string
  ledgerId?: number
  debit: string | null
  credit: string | null
  children?: BalanceDetails[]
}

export const getTrialBalanceData = async (
  company_id: string,
  fy_name: string,
  from_date: string | undefined,
  to_date: string | undefined
): Promise<TrialBalanceResp | undefined> => {
  try {
    const res = await axios.post(
      `${API_URL}/accounting/transaction/report/trial-balance/test`,
      {
        company_id: +company_id,
        fy_name,
        from_date,
        to_date,
        report_name: 'trial_balance',
        report_type: null,
      },
      {
        validateStatus: () => true,
      }
    )

    if (!res) {
      throw new Error('Internal Server Error')
    }

    if (res.data && res.data.status && res.data.status === 'error') {
      throw new Error(res.data.message)
    }
    // return transformTrialBalanceData(TRIAL_BALANCE_SAMPLE_RESPONSE)
    // console.log(transformTrialBalanceData(res.data))
    return transformTrialBalanceData(res.data)
    return res.data
  } catch (error: any) {
    // console.log(error.message)
    if (error) throw error
  }
}

export interface TrialBalLedgerListParams {
  company_id: number
  fy_name: string
  from_date?: string | null
  to_date?: string | null
  voucher_type?: string | null
  // parent_ledger: string
  // sub_ledger?: string
  // parent_group?: string
  // sub_group?: string
  ledger_id: number
  group_id: number | null
}

export interface TrialBalLedgerListResp {
  transactionList: TransactionList[]
  parentLedger: string | null
  subLedger: string | null
  parentGroup: string | null
  subGroup: string | null
  toDate: number
  fromDate: string
  openingBalance: number
  closingBalance: number
  debitAmount: number
  creditAmount: number
  transactionType: string
}

export interface TransactionList {
  voucherNumber: number
  voucherType: string | null
  voucherDate: string
  amount: number
  exchangeMultiplier: number
  reportingCurrency: string
  transactingCurrency: string | null
  transactionType: string
  invoiceDetails: InvoiceDetails[]
  transactionDetails: TransactionDetail[]
}

export interface InvoiceDetails {
  invoiceNumber: string
  invoiceAmount: number
  invoiceDescription: string
  settledInvoiceAmount: number
  isInvoiceSettled: boolean
}

export interface TransactionDetail {
  ledgerCode: string
  parentLedger: string
  subLedger: string
  transactionAmount: number
  transactionType: string
}

export const getTrialBalanceLedgersList = async ({
  company_id,
  fy_name,
  ledger_id,
  voucher_type = null,
  from_date = null,
  to_date = null,
  group_id,
}: TrialBalLedgerListParams): Promise<TransformedResponse | undefined> => {
  return await axios
    .post(`${API_URL}/accounting/transaction/view`, {
      company_id,
      fy_name,
      ledger_id,
      voucher_type,
      from_date,
      to_date,
      group_id,
    })
    .then((response: AxiosResponse<ApiResponse>) => transformResponse(response.data))
}

export interface TrialBalGroupListParams {
  company_id: number
  fy_name: string
  group_id: number
  from_date?: string | null
  to_date?: string | null
}
export interface TrialBalanceGroupsListResp {
  ledgerList: LedgerList[]
  // parentLedger: string | null
  // subLedger: string | null
  // parentGroup: string
  // subGroup: string | null
  // toDate: string
  // fromDate: string
}

export interface LedgerList {
  ledgerId: number
  groupId: number
  ledgerCode: string
  parentLedger: string
  subLedger: string
  reportingCurrency: string
  transactingCurrency: null | string
  reportingCurrencyAmount: number
  transactingCurrencyAmount: number | null
  transactionType?: string
  parentGroup?: string | null
  subGroup?: string | null
}

export const getTrialBalanceGroupsList = async ({
  ...params
}: TrialBalGroupListParams): Promise<TrialBalanceGroupsListResp | undefined> => {
  return await axios
    .post(`${API_URL}/product-master/ledger`, params)
    .then((response: AxiosResponse) => {
      return {
        ledgerList: response.data.map((_: any) => ({
          ledgerId: _.ledger_id,
          groupId: _.group_id,
          ledgerCode: _.ledger_code,
          parentLedger: _.parent_ledger,
          subLedger: _.sub_ledger,
          reportingCurrency: _.currency,
          transactingCurrency: null,
          reportingCurrencyAmount: _.closing_balance || 0,
          transactingCurrencyAmount: null,
          transactionType: _.transaction_type,
          parentGroup: _.parent_group,
          subGroup: _.sub_group,
        })),
      }
    })
}

export interface DownloadTrialBalanceParams {
  company_id: string
  fy_name: string
  from_date?: string
  to_date?: string
}

export const downloadTrialBalance = async ({
  company_id,
  fy_name,
  ...params
}: DownloadTrialBalanceParams): Promise<ArrayBufferLike | undefined> => {
  return await axios
    .get(`${API_URL}/accounting/report/download/trial-balance/${company_id}/${fy_name}`, {
      params,
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })
    .then((response: AxiosResponse<ArrayBufferLike>) => response.data)
    .then((response: ArrayBufferLike) => response)
}
